import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { HelpCircle } from "lucide-react";

const FAQS = [
  {
    q: "How long does a website build take?",
    a: "Starter Websites typically launch in 5–10 business days. Lead Generation Websites in 2–3 weeks. Complete Marketing Systems run 3–5 weeks depending on scope. We commit to dates up front and meet them.",
  },
  {
    q: "Do I own the website when it's done?",
    a: "Yes. You own the domain, the design, the copy, and all of the content outright. We hand over full administrative access on day one. There is no platform lock-in.",
  },
  {
    q: "What's included in the price?",
    a: "Design, copywriting, technical build, on-page SEO setup, mobile optimization, and a launch checklist. Hosting and domain are separate (we'll recommend providers, or we can manage them on a monthly retainer).",
  },
  {
    q: "What if I don't like the design?",
    a: "Each package includes two structured rounds of revisions. We start with mood boards and direction before any design work, so by the time we build, we're refining — not guessing.",
  },
  {
    q: "Will my site actually show up on Google?",
    a: "Every package includes proper SEO foundations — site speed, schema, meta tags, and local optimization. Visibility compounds with time; pair the build with a Local SEO retainer if you want to accelerate rankings.",
  },
  {
    q: "I'm not technical at all. Will I be able to make changes?",
    a: "Yes. Every site is delivered with a simple editor and a short video walkthrough. For clients who'd rather not touch it, we offer an affordable monthly maintenance plan.",
  },
  {
    q: "Do you only work with local businesses?",
    a: "Local service businesses are our specialty — but we work with professional services, restaurants, real estate, healthcare practices, and e-commerce as well. Each industry has a tailored foundation.",
  },
  {
    q: "What happens after launch?",
    a: "We offer a 30-day post-launch support window included with every build. Beyond that, our monthly retainers cover ongoing updates, SEO, reputation management, and marketing — adjustable as your business grows.",
  },
  {
    q: "Can I pay in installments?",
    a: "Yes. A deposit secures your slot, with the balance billed in stages tied to milestones. Stripe handles the transactions — clean, secure, and straightforward.",
  },
];

export default function FAQ() {
  return (
    <section id="faq" data-testid="faq-section" className="relative py-24 md:py-32 border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="overline mb-4 flex items-center gap-2">
              <HelpCircle size={14} /> // STRAIGHT_ANSWERS
            </div>
            <h2 className="font-heading font-black text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[1.05] mb-6">
              Common<br />
              <span className="text-neon-cyan neon-text-cyan">questions.</span>
            </h2>
            <p className="text-white/60 text-base md:text-lg max-w-md">
              The things most people want to know before they call. If something isn&apos;t here, send a message — we&apos;ll answer personally.
            </p>
          </div>

          <div className="lg:col-span-7">
            <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
              {FAQS.map((item, i) => (
                <AccordionItem
                  key={i}
                  value={`item-${i}`}
                  data-testid={`faq-item-${i}`}
                  className="border-b border-white/10 last:border-b-0"
                >
                  <AccordionTrigger className="text-left font-heading font-bold text-base md:text-lg hover:text-neon-cyan transition-colors hover:no-underline py-5">
                    <span className="flex items-start gap-4">
                      <span className="font-mono text-xs text-white/30 mt-1 flex-shrink-0">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span>{item.q}</span>
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="text-white/60 text-sm md:text-base leading-relaxed pl-10 pb-5">
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </div>
    </section>
  );
}
